import { useRouter, useSearch } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import { useFragment, type FragmentType } from "@repo/graphql-types";
import type { RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragmentFragment } from "@repo/graphql-types/graphql";
import { DashboardSectionHeader } from "@/components/dashboard/dashboard-section-header";
import { DashboardSectionEmptyState } from "@/components/dashboard/dashboard-section-empty-state";
import { DashboardInformationalDrawer } from "@/components/dashboard/dashboard-informational-drawer";
import {
  ChartRoot,
  ChartBarContainer,
  ChartBarItem,
  ChartBarLegend,
} from "@/components/charts";
import { DashboardSectionTotalNumber } from "@/components/dashboard/dashboard-section-total-number";
import { trackEvent } from "@/lib/tracking";
import { RescuedAppointmentsType } from "@/enums/rescued-appointments-types";
import {
  fillRescuedAppointmentsMissingMonths,
  sortDataByMonth,
  groupRescuedAppointmentDataByMonth,
} from "@/lib/dashboard";
import { AssistFeatures } from "@/enums/assist-features";
import { DashboardFeatureStatusBadge } from "@/components/dashboard/dashboard-feature-status-badge";

export const RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragment =
  graphql(/* GraphQL */ `
    fragment RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragment on AnalyticsMembroTotalDeAgendamentosResgatadosPorFluxo {
        data {
            month: mes
            rescuedAppointmentType: fluxo
            appointmentsRescuedSum: agendamentosRecuperados
            sentMessagesSum: totalMensagens
        }
    }
  `);

interface RescuedAppointmentDashboardNoShowAppointmentsSectionProps {
  data: FragmentType<
    typeof RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragment
  >;
  selectedMonth: string;
  onSelectedMonthChange: (newMonth: string) => void;
  fromDate: string;
  toDate: string;
}

export const RescuedAppointmentDashboardNoShowAppointmentsSection = ({
  data,
  selectedMonth,
  onSelectedMonthChange,
  fromDate,
  toDate,
}: RescuedAppointmentDashboardNoShowAppointmentsSectionProps): JSX.Element | null => {
  const fragmentData = useFragment(
    RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragment,
    data,
  );

  const router = useRouter();
  const searchParams = useSearch({
    from: "/settings/assist/rescued-appointment-overview/",
  });

  const onOpenChange = (open: boolean): void => {
    const search = {
      ...searchParams,
    };

    if (open) {
      search.showNoShowAppointmentsDrawer = open;

      trackEvent("Definição Gráfico Aberta", {
        grafico: "Faltas recuperadas",
      });

      void router.navigate({ search });
    } else if (searchParams.showNoShowAppointmentsDrawer) {
      router.history.back();
    }
  };

  const currentMonthData = fragmentData.data.filter(
    (item) => item?.month === selectedMonth,
  );

  const noShowAppointment = currentMonthData.find(
    (item) => item?.rescuedAppointmentType === RescuedAppointmentsType.NoShow,
  );

  return (
    <div className="my-8 flex flex-col gap-8">
      <DashboardSectionHeader
        onClick={onOpenChange}
        statusBadge={
          <DashboardFeatureStatusBadge
            featureId={AssistFeatures.RescueNoShowAppointment}
          />
        }
      >
        Faltas recuperadas
      </DashboardSectionHeader>
      {fragmentData.data.length === 0 ? (
        <DashboardSectionEmptyState />
      ) : (
        <>
          <DashboardSectionTotalNumber
            total={Number(noShowAppointment?.appointmentsRescuedSum ?? 0)}
            text="Faltas recuperadas"
          />

          <RescuedAppointmentDashboardNoShowAppointmentsSectionChart
            fragmentData={fragmentData}
            selectedMonth={selectedMonth}
            onSelectedMonthChange={onSelectedMonthChange}
            onOpenChange={onOpenChange}
            fromDate={fromDate}
            toDate={toDate}
          />
        </>
      )}

      <DashboardInformationalDrawer
        open={Boolean(searchParams.showNoShowAppointmentsDrawer)}
        onOpenChange={onOpenChange}
        drawerTitle="Faltas recuperadas"
      >
        <div className="flex flex-col gap-2 text-md text-neutral-500">
          <p>
            Pacientes que faltaram à consulta e reagendaram somente após receberem a
            mensagem de recuperação, enviada 24 horas após a falta.
          </p>
          <p>
            Consideramos como recuperado quem reagenda em até 2 dias após o recebimento da
            mensagem.
          </p>
        </div>
      </DashboardInformationalDrawer>
    </div>
  );
};

interface RescuedAppointmentDashboardNoShowAppointmentsSectionChartProps {
  fragmentData: RescuedAppointmentDashboardNoShowAppointmentsSectionQueryFragmentFragment;
  selectedMonth: string;
  onSelectedMonthChange: (newMonth: string) => void;
  onOpenChange: (open: boolean) => void;
  fromDate: string;
  toDate: string;
}

export const RescuedAppointmentDashboardNoShowAppointmentsSectionChart = ({
  fragmentData,
  selectedMonth,
  onSelectedMonthChange,
  onOpenChange,
  fromDate,
  toDate,
}: RescuedAppointmentDashboardNoShowAppointmentsSectionChartProps): JSX.Element => {
  const chartKey = "Faltas Recuperadas";

  const noShowAppointmentData = fragmentData.data
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .filter((item) => item.rescuedAppointmentType === RescuedAppointmentsType.NoShow);

  const groupedData = groupRescuedAppointmentDataByMonth(noShowAppointmentData, chartKey);

  const filledGroupedData = fillRescuedAppointmentsMissingMonths(
    groupedData,
    fromDate,
    toDate,
    chartKey,
  );

  const sortedChartData = sortDataByMonth(filledGroupedData);

  const chartData = sortedChartData.map((item) => ({
    mes: item.month,
    "Mensagens Enviadas": item.sentMessagesSum,
    [chartKey]: item[chartKey],
  }));

  return (
    <div data-testid="rescued-no-show-appointments-section-chart">
      <ChartRoot
        selectedKey={selectedMonth}
        onSelectedKeyChange={onSelectedMonthChange}
        data={chartData}
      >
        <ChartBarContainer height={299} XAxisKey="mes">
          <ChartBarItem dataKey={chartKey} fill="#72ABBC" disabledFill="#EDF0F2" />
          <ChartBarItem
            dataKey="Mensagens Enviadas"
            fill="#003C52"
            disabledFill="#D5DADF"
          />

          <ChartBarLegend
            onClick={() => onOpenChange(true)}
            reverseOrder
            flexDirection="column"
          />
        </ChartBarContainer>
      </ChartRoot>
    </div>
  );
};
